import { toCanvas } from 'html-to-image';
import { ScreenshotData, localLogStore } from './local-log-store';
import { ConsoleKey } from './constants';

const SCREENSHOT_CONFIG = {
  quality: 0.8,
  pixelRatio: 1,
};

/**
 * Generates a unique ID for screenshots
 */
const generateScreenshotId = (): string =>
  `screenshot_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/**
 * Captures a screenshot of the current viewport as a WebP
 * @returns Promise that resolves to screenshot data or null if capture fails
 */
export const captureViewportScreenshot =
  async (): Promise<ScreenshotData | null> => {
    try {
      const viewportSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      const canvas = await toCanvas(document.body, {
        pixelRatio: SCREENSHOT_CONFIG.pixelRatio,
        width: viewportSize.width,
        height: viewportSize.height,
        skipFonts: true,
        filter: (node) => {
          // Skip elements that might cause CORS issues
          if (node.tagName === 'IMG') {
            const img = node as HTMLImageElement;
            if (
              img.crossOrigin === 'anonymous' ||
              img.src.startsWith(window.location.origin)
            )
              return true;
            return false;
          }
          return true;
        },
      });

      const dataUrl = canvas.toDataURL('image/webp', SCREENSHOT_CONFIG.quality);

      return {
        id: generateScreenshotId(),
        dataUrl,
      };
    } catch (error) {
      console.warn(
        `${ConsoleKey} Warning: Failed to capture screenshot:`,
        error,
      );
      return null;
    }
  };

/**
 * Checks if screenshot capture is supported in the current environment
 */
export function isScreenshotSupported(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

/**
 * Opens a screenshot in a new tab by its array index
 * @param index - The array index of the screenshot to open
 */
export async function openScreenshot(index: number) {
  const { screenshots } = localLogStore.getState();
  const screenshot = screenshots[index];

  if (!screenshot) {
    console.warn(
      `${ConsoleKey} Warning: Screenshot at index ${index} not found`,
    );
    return;
  }

  try {
    const response = await fetch(screenshot.dataUrl);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const newWindow = window.open(blobUrl, '_blank');

    if (newWindow) {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }
  } catch (error) {
    console.warn(
      `${ConsoleKey} Warning: Failed to open screenshot in new tab:`,
      error,
    );
  }
}
